import React, { useEffect, useState } from "react";
import { pickClaim, checkAnswer } from "@/lib/misinfoBank";
import Modal from "./Modal";

const LEVELS = [
  { key: "easy",   label: "🌱 Apprentice",       streakToUnlock: 0,  desc: "Famous facts & common myths" },
  { key: "hard",   label: "⚡ Sciencer",          streakToUnlock: 3,  desc: "Counter-intuitive science + tricky claims" },
  { key: "genius", label: "🧠 Neuro-Cardio Genius", streakToUnlock: 6,  desc: "Deep technical truths — wild but real" },
];

export default function MisinfoBusterModal({ open, onClose, handle }) {
  const [claim, setClaim] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [score, setScore] = useState({ correct: 0, total: 0, streak: 0, bestStreak: 0 });
  const [difficulty, setDifficulty] = useState("easy");
  const [autoMode, setAutoMode] = useState(true); // auto-progress on win streak

  const newClaim = (forceDifficulty = null) => {
    const d = forceDifficulty || difficulty;
    setLoading(true);
    setResult(null);
    setClaim(null);
    // Tiny artificial delay so the transition doesn't feel jarring
    setTimeout(() => {
      try { setClaim(pickClaim(d)); }
      catch { setClaim(null); }
      finally { setLoading(false); }
    }, 180);
  };

  useEffect(() => { if (open && !claim && !loading) newClaim(); }, [open]); // eslint-disable-line

  const submit = (answer) => {
    if (!claim) return;
    const r = checkAnswer(claim, answer);
    setResult(r);
    setScore((s) => {
      const newStreak = r.correct ? s.streak + 1 : 0;
      const newScore = {
        correct: s.correct + (r.correct ? 1 : 0),
        total: s.total + 1,
        streak: newStreak,
        bestStreak: Math.max(s.bestStreak, newStreak),
      };
      // Auto-progression: bump difficulty on streaks of 3 and 6
      if (autoMode && r.correct) {
        if (newStreak === 3 && difficulty === "easy") setDifficulty("hard");
        else if (newStreak === 6 && difficulty === "hard") setDifficulty("genius");
      }
      return newScore;
    });
  };

  const handleLevelClick = (lvl) => {
    if (lvl.streakToUnlock > 0 && score.bestStreak < lvl.streakToUnlock) return; // locked
    setDifficulty(lvl.key);
    setAutoMode(false);
    newClaim(lvl.key);
  };

  const currentLevel = LEVELS.find(l => l.key === difficulty) || LEVELS[0];

  return (
    <Modal open={open} onClose={onClose} title="🔍 Clickbait Slayer" testId="misinfo-modal" wide tint="cyan">
      <p className="text-[var(--ink-soft)] text-sm mb-3">
        Sean's rule: <strong className="text-[var(--ink)]">Credible · Clear · Concise.</strong> Real or FAKE?
      </p>

      {/* Difficulty picker — locks unlock on streak */}
      <div className="grid grid-cols-3 gap-2 mb-4" data-testid="misinfo-levels">
        {LEVELS.map((lvl) => {
          const locked = lvl.streakToUnlock > 0 && score.bestStreak < lvl.streakToUnlock;
          const active = lvl.key === difficulty;
          return (
            <button
              key={lvl.key}
              type="button"
              onClick={() => handleLevelClick(lvl)}
              disabled={locked}
              data-testid={`misinfo-level-${lvl.key}`}
              className="card-flat text-left"
              style={{
                background: active ? "var(--orange)" : locked ? "#f3f4f6" : "#fff",
                borderColor: "var(--ink)",
                boxShadow: active ? "3px 3px 0 0 var(--ink)" : "2px 2px 0 0 var(--ink)",
                opacity: locked ? 0.55 : 1,
                cursor: locked ? "not-allowed" : "pointer",
                padding: "0.55rem 0.65rem",
              }}
            >
              <div className="font-fun text-sm" style={{ color: active ? "#fff" : "var(--ink)" }}>{lvl.label}</div>
              <div className="label-tag mt-0.5" style={{ color: active ? "#fff" : "var(--ink-soft)" }}>
                {locked ? `🔒 unlock at ${lvl.streakToUnlock} streak` : lvl.desc}
              </div>
            </button>
          );
        })}
      </div>

      <div className="card-flat tint-blue mb-4 min-h-[160px] flex flex-col justify-center">
        {loading || !claim ? (
          <div className="text-center text-[var(--ink-soft)] font-mono text-sm">⚛️ generating science claim...</div>
        ) : (
          <>
            <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
              <div className="eyebrow text-[var(--cyan)]">// topic: {claim.category}</div>
              <span className="pill pill-sun text-[0.62rem]" data-testid="misinfo-current-level">{currentLevel.label}</span>
            </div>
            <div className="font-display text-xl sm:text-2xl text-[var(--ink)] leading-snug">"{claim.claim}"</div>
          </>
        )}
      </div>

      {!result ? (
        <div className="grid grid-cols-2 gap-3">
          <button onClick={() => submit(true)} disabled={!claim || loading} className="btn btn-mint" data-testid="misinfo-real">✅ REAL</button>
          <button onClick={() => submit(false)} disabled={!claim || loading} className="btn btn-heart" data-testid="misinfo-fake">⚠️ FAKE</button>
        </div>
      ) : (
        <div className={`card-flat ${result.correct ? "tint-green" : "tint-red"}`} data-testid="misinfo-result">
          <div className="font-display text-2xl mb-2 text-[var(--ink)]">
            {result.verdict}
            {result.correct && score.streak >= 3 && (
              <span className="ml-2 pill pill-red animate-pulse" data-testid="misinfo-streak-pill">🔥 {score.streak} streak</span>
            )}
          </div>
          <div className="label-tag mb-2">// answer: {result.is_real ? "REAL 🟢" : "FAKE 🔴"}</div>
          <p className="text-[var(--ink)] text-sm leading-relaxed mb-4">{result.explanation}</p>
          {/* Level-up congrats */}
          {autoMode && result.correct && score.streak === 3 && difficulty === "hard" && (
            <div className="mb-3 p-3 rounded-xl" style={{ background: "#fff1de", border: "2.5px solid var(--ink)" }} data-testid="misinfo-levelup-hard">
              <strong className="text-[var(--orange)]">🎉 Level up!</strong> You unlocked <strong>⚡ Sciencer</strong> — claims just got harder.
            </div>
          )}
          {autoMode && result.correct && score.streak === 6 && difficulty === "genius" && (
            <div className="mb-3 p-3 rounded-xl" style={{ background: "#fff1de", border: "2.5px solid var(--ink)" }} data-testid="misinfo-levelup-genius">
              <strong className="text-[var(--red)]">🚀 Neuro-Cardio Genius unlocked!</strong> Welcome to the deep end.
            </div>
          )}
          <button onClick={() => newClaim()} className="btn btn-primary" data-testid="misinfo-next">🎲 Next Claim</button>
        </div>
      )}

      <div className="mt-4 flex justify-between items-center text-xs hud-label flex-wrap gap-2">
        <span data-testid="misinfo-score">// score: {score.correct}/{score.total} · best streak: {score.bestStreak}</span>
        <span>Sean-approved 🧠🫀</span>
      </div>
    </Modal>
  );
}
